import React from 'react';
import {
  Box,
  Typography,
  Stack,
  Chip,
  Divider,
  Avatar,
  LinearProgress,
  Badge,
} from '@mui/material';
import { 
  Email as EmailIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import { Card } from '../../UI/Card';
import { AvatarUploader } from './AvatarUploader';
import { UserInfoCardProps } from './types';
import { getUserRoleLabel, getIntegratedUserStatus } from './utils';
import { formatDate } from '../../../utils/dateUtils';
import { AvatarUploadResult } from '../../../types/profile';

export const UserInfoCard: React.FC<UserInfoCardProps> = ({ user }) => {
  // プロフィール完了度の計算（アバターも含む）
  const calculateProfileCompletion = () => {
    const fields = [
      user.fullName,
      user.bio,
      user.phone,
      user.organization,
      user.avatar,
    ];
    const filledFields = fields.filter(field => field !== undefined && field !== null && field !== '').length;
    return Math.round((filledFields / fields.length) * 100);
  };

  const profileCompletion = calculateProfileCompletion();

  const handleAvatarUpload = (result: AvatarUploadResult) => {
    console.log('Avatar uploaded successfully:', result);
    // TODO: プロフィールを更新してアバターURLを反映
  };

  const handleAvatarError = (error: string) => {
    console.error('Avatar upload error:', error);
    // TODO: エラートーストを表示
  };

  return (
    <Card padding="large">
      <Stack spacing={3} alignItems="center">
        {/* Avatar Section - アップロード機能付き */}
        <AvatarUploader
          currentAvatarUrl={user.avatar}
          userName={user.fullName || user.email}
          onUploadComplete={handleAvatarUpload}
          onError={handleAvatarError}
          size="large"
        />

        {/* User Basic Info */}
        <Stack spacing={1} alignItems="center">
          <Typography variant="h5" fontWeight="bold" textAlign="center">
            {user.fullName || 'ユーザー名未設定'}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EmailIcon fontSize="small" color="action" />
            <Typography variant="body2" color="text.secondary">
              {user.email}
            </Typography>
          </Box>

          {/* Role and Status Chips */}
          <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent="center">
            <Chip
              label={getUserRoleLabel(user.userRole)}
              color={user.userRole === 'admin' ? 'error' : 'primary'}
              size="small"
            />
            <Chip
              label={getIntegratedUserStatus(user.status, user.isVerified || false).label}
              color={getIntegratedUserStatus(user.status, user.isVerified || false).color}
              size="small"
              variant="outlined"
            />
          </Stack>
        </Stack>

        <Divider />

        {/* Metadata */}
        <Box>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            登録日
          </Typography>
          <Typography variant="body1">
            {formatDate(user.createdAt)}
          </Typography>
        </Box>

        <Box>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            最終更新
          </Typography>
          <Typography variant="body1">
            {formatDate(user.updatedAt)}
          </Typography>
        </Box>

        <Divider />

        {/* プロフィール完了度 */}
        <Box sx={{ width: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              プロフィール完了度
            </Typography>
            <Typography variant="body2" color="primary" fontWeight="bold">
              {profileCompletion}%
            </Typography>
          </Box>
          <LinearProgress 
            variant="determinate" 
            value={profileCompletion} 
            sx={{ 
              height: 8, 
              borderRadius: 4,
              backgroundColor: 'grey.200',
              '& .MuiLinearProgress-bar': {
                borderRadius: 4,
                backgroundColor: profileCompletion >= 80 ? 'success.main' : 
                               profileCompletion >= 50 ? 'warning.main' : 'error.main'
              }
            }}
          />
        </Box>

        {user.isVerified && (
          <Box>
            <Chip
              label="メールアドレス認証済み"
              color="success"
              variant="outlined"
              size="small"
            />
          </Box>
        )}
      </Stack>
    </Card>
  );
}; 